import React from "react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatPrice } from "@/utils/monetary-formatter";

import { TDashboardChartResponse, TDashboardProductType } from "../dashboard.types";

interface SalesAreaChartProps {
  title?: string;
  productType: TDashboardProductType;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  chartData: TDashboardChartResponse | null;
  isLoading: boolean;
}

const months = [
  { key: "january", short: "Jan", full: "Janeiro" },
  { key: "february", short: "Fev", full: "Fevereiro" },
  { key: "march", short: "Mar", full: "Março" },
  { key: "april", short: "Abr", full: "Abril" },
  { key: "may", short: "Mai", full: "Maio" },
  { key: "june", short: "Jun", full: "Junho" },
  { key: "july", short: "Jul", full: "Julho" },
  { key: "august", short: "Ago", full: "Agosto" },
  { key: "september", short: "Set", full: "Setembro" },
  { key: "october", short: "Out", full: "Outubro" },
  { key: "november", short: "Nov", full: "Novembro" },
  { key: "december", short: "Dez", full: "Dezembro" }
];

const chartConfig = {
  month: {
    label: "Mês",
  },
  success: {
    label: "Vendas com Sucesso",
    color: "hsl(142, 76%, 36%)", // green-500
  },
  failure: {
    label: "Vendas com Falha",
    color: "hsl(0, 84%, 60%)", // red-500
  },
} satisfies ChartConfig;

export function SalesAreaChart({
  title = "Vendas",
  productType,
  selectedYear,
  setSelectedYear,
  chartData,
  isLoading,
}: SalesAreaChartProps) {
  // Anos disponíveis (2025 a 2030)
  const availableYears = [2025, 2026, 2027, 2028, 2029, 2030];

  // Mapeamento dos nomes dos tipos de produto
  const productTypeLabels: Record<TDashboardProductType, string> = {
    all: "Planos",
    insurance_group_life: "Seguro VG",
    insurance_personal_assistance: "Seguro AP",
    insurance_assistance: "Assistências",
  };

  // Gerar título dinâmico baseado no tipo de produto
  const chartTitle = React.useMemo(() => {
    if (productType === "all") {
      return `Planos vendidos em ${selectedYear}`;
    }
    const productLabel = productTypeLabels[productType] || "Produto";
    return `Vendas de ${productLabel} de ${selectedYear}`;
  }, [productType, selectedYear]);

  // Preparar dados para o gráfico a partir da resposta da API
  const chartDataFormatted = React.useMemo(() => {
    if (!chartData) {
      return months.map((month) => ({
        month: month.short,
        monthFull: month.full,
        success: 0,
        failure: 0,
        successValue: 0,
        failureValue: 0,
      }));
    }

    return months.map((month) => {
      const monthData = chartData[month.key as keyof TDashboardChartResponse];
      return {
        month: month.short,
        monthFull: month.full,
        success: monthData?.purchases_approved?.count || 0,
        failure: monthData?.purchases_denied?.count || 0,
        successValue: monthData?.purchases_approved?.total_value || 0,
        failureValue: monthData?.purchases_denied?.total_value || 0,
      };
    });
  }, [chartData]);

  // Calcular totais
  const totalSuccess = React.useMemo(() => {
    return chartDataFormatted.reduce((sum, item) => sum + item.success, 0);
  }, [chartDataFormatted]);

  const totalFailure = React.useMemo(() => {
    return chartDataFormatted.reduce((sum, item) => sum + item.failure, 0);
  }, [chartDataFormatted]);

  const formatQuantity = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <Card className="pt-0 h-full w-full flex flex-col min-h-0">
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row flex-shrink-0">
        <div className="grid flex-1 gap-1">
          <div className="flex items-center gap-2">
            <CardTitle>{chartTitle}</CardTitle>
          </div>
          <CardDescription className="flex gap-4">
            <span className="text-green-500">
              {formatQuantity(totalSuccess)} sucessos
            </span>
            <span className="text-red-500">
              {formatQuantity(totalFailure)} falhas
            </span>
          </CardDescription>
        </div>
        <Select
          value={String(selectedYear)}
          onValueChange={(value) => setSelectedYear(Number(value))}
        >
          <SelectTrigger
            className="w-[160px] rounded-lg"
            aria-label="Selecionar ano"
          >
            <SelectValue placeholder="Selecionar ano" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {availableYears.map((year) => (
              <SelectItem key={year} value={String(year)} className="rounded-lg">
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6 flex-1 min-h-0">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-full w-full"
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-muted-foreground">Carregando...</div>
            </div>
          ) : (
            <AreaChart data={chartDataFormatted}>
            <defs>
              <linearGradient id="fillSuccess" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-success)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-success)"
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="fillFailure" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-failure)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-failure)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
            />
            <ChartTooltip
              cursor={false}
              content={(props) => {
                if (!props.active || !props.payload || props.payload.length === 0) {
                  return null;
                }

                const payload = props.payload[0];
                const data = payload.payload as typeof chartDataFormatted[0];
                const monthName = data?.monthFull || props.label;

                const successItem = props.payload.find((item) => item.dataKey === "success");
                const failureItem = props.payload.find((item) => item.dataKey === "failure");

                return (
                  <div className="rounded-lg border border-border/50 bg-background px-4 py-3 text-xs shadow-xl">
                    <div className="font-medium mb-3">{monthName}</div>
                    <div className="grid gap-2.5">
                      {successItem && (
                        <div className="flex items-center gap-3">
                          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(142,76%,36%)]" />
                          <div className="flex-1 flex justify-between gap-4">
                            <span className="text-muted-foreground">Vendas com Sucesso</span>
                            <div className="text-right flex flex-col gap-0.5">
                              <div className="font-mono font-medium tabular-nums text-foreground">
                                {Number(successItem.value).toLocaleString()} vendas
                              </div>
                              {data?.successValue !== undefined && data.successValue > 0 && (
                                <div className="text-muted-foreground text-[10px]">
                                  {formatPrice(data.successValue)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {failureItem && (
                        <div className="flex items-center gap-3">
                          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(0,84%,60%)]" />
                          <div className="flex-1 flex justify-between gap-4">
                            <span className="text-muted-foreground">Vendas com Falha</span>
                            <div className="text-right flex flex-col gap-0.5">
                              <div className="font-mono font-medium tabular-nums text-foreground">
                                {Number(failureItem.value).toLocaleString()} vendas
                              </div>
                              {data?.failureValue !== undefined && data.failureValue > 0 && (
                                <div className="text-muted-foreground text-[10px]">
                                  {formatPrice(data.failureValue)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            />
            <Area
              dataKey="success"
              type="natural"
              fill="url(#fillSuccess)"
              stroke="var(--color-success)"
              strokeWidth={2}
            />
            <Area
              dataKey="failure"
              type="natural"
              fill="url(#fillFailure)"
              stroke="var(--color-failure)"
              strokeWidth={2}
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
          )}
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

